import { AROM_ASSESSMENT, mCTSIB_ASSESSMENT } from "../models/Enums";

import {
  API_URL,
  AromMenuOptions,
  ASSESSMENT_STATUSES,
  AWS_CONFIG,
  distace,
  DL_MODELS,
  S3_BUCKET,
} from "../movenet/params";
import { mCTSIBAssessmentStates } from "./assessment_states";
import { mCTSIBAudioPlayer } from "./audio-player";
import { config } from "./env_config";
import { MoveNetPoseWrapper } from "./pose-wrapper";

import { SitStandProcessing } from "./sitStandProcessing";
import { GestureDetector } from "./start_gesture_detector";
import {
  startiOSVoiceListening,
  stopiOSVoiceListening,
} from "../utils/mobile-bridge";
import { recordShoulderSway } from "./record_mCTSIB_values";
import { calculateSubAssessmentMetrics, calculatePhaseScore } from "./mCTSIB_score_calculations";
import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { MultiPoseUtility } from "./multipose_utility";
import { PatientSelection } from "./patient_selection";
import { SentryLogging } from "./sending_sentry_logs";
import shoePrintSrc from "../assets/images/shoe_print.png";

const TIME_LIMIT_SEC = 30; // Time limit per sub-assessment in seconds (matching 3D for comparison)
const MAX_SWAY_INCHES = 6; // Maximum sway radius before triggering error (matches SwayVisualizer circle)
const cv = window.cv;
const s3Client = new S3Client(AWS_CONFIG);

const FEET_BOX_LENGTH = 120;

export const Open_Eyes = [];
export const Closed_Eyes = [];
export const Open_Eyes_With_Foam = [];
export const Closed_Eyes_With_Foam = [];

export class mCTSIB {
  /**
   * Initializes the Upper Body AROM assessment with necessary callbacks,
   * UI elements, and default state values.
   * @param {Object} camera - Camera interface for video input and drawing.
   * @param {Function} switch_to_single_pose - Callback to switch to single-pose model.
   * @param {Function} reset_pose_estimator - Callback to reset pose estimator.
   * @param {Function} switch_to_multi_pose - Callback to switch back to multi-pose model.
   */
  constructor(
    calibration,
    camera,
    switch_to_single_pose,
    reset_pose_estimator,
    switch_to_multi_pose
  ) {
    this.switch_to_single_pose_callback = switch_to_single_pose;
    this.switch_to_multi_pose_callback = switch_to_multi_pose;
    this.reset_pose_estimator = reset_pose_estimator;
    this.camera = camera;
    this.current_state = mCTSIBAssessmentStates.calculatingDepth;
    this.calibration = calibration;
    this.gesture_detector = new GestureDetector();
    this.sit_stand_processing = new SitStandProcessing();
    this.end_time = 0;
    this.first_frame = true;
    this.audio_player = new mCTSIBAudioPlayer();
    this.therapist_id = -1;
    this.patient_id = -1;
    this.patient_ID_changed = false;
    this.track_patient = false;
    this.start_time = 0;
    this.is_calibrated = false;
    this.calibration_error_occured = false;
    this.initialize_calibration_error_occured = true;
    this.feet_box_x_min = 0;
    this.feet_box_x_max = 0;
    this.feet_box_y_min = 0;
    this.feet_box_y_max = 0;
    this.frames_with_no_patient = 0;
    this.frames_with_patient_outside_extended_box = 0;
    this.insetoutputCanvas = document.getElementById("insetoutput");
    this.are_you_there_flag = true;
    this.are_you_there_timer = 0;
    this.current_time = 0;
    this.first_frame_calibration_2 = true;
    this.time_calibration_2 = 0;
    this.inactive_for_60_seconds = false;
    this.start_timer_reference = 0;
    this.within_bound_start_flag = false;
    this.assessment_timer = 0;
    this.first_frame_time = 0;
    this.angle_value = -1;
    this.patient_within_box_check = false;
    this.patient_within_box_timer = 0;
    this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Open_Eyes;
    this.pathwayBlocked = true;
    this.withInBoxAudio = true;
    this.pathwayCalibrated = true;
    this.is_path_clear = true;
    this.is_api_called = null;
    this.pathBlockedCount = null;
    this.two_second_start_time = 0;

    // Offscreen canvas for capturing frames to pass to the depth model
    let canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    this.offscreen_canvas = canvas;
    this.offscreen_canvas_ctx = this.offscreen_canvas.getContext("2d", {
      willReadFrequently: true,
    });
    this.canvas = document.getElementById("output");
    this.ctx = this.canvas.getContext("2d");
    this.side_margin = 0.4;

    let videoWidth = 1080;
    let videoHeight = 1920;
    this.options = {
      sx: 0,
      sy: 0,
      sw: videoWidth,
      sh: videoHeight,
    };
    this.display_modalFaceCamera = false;
    this.play_beep_sound = true;
    this.handFlag = true;
    this.assessment_function = null;
    if (this.insetoutputCanvas) {
      this.insetoutputCanvas.style.display = "none";
    }
    let patientServerID = localStorage.getItem("patientServerID")
      ? localStorage.getItem("patientServerID")
      : "000";
    let userID = localStorage.getItem("UserID")
      ? localStorage.getItem("UserID")
      : "000";
    let businessCode = localStorage.getItem("BusinessCode")
      ? localStorage.getItem("BusinessCode")
      : "000";
    this.sentry_prefix =
      `mCTSIB${patientServerID}_${userID}_${businessCode}_` +
      new Date().toISOString();
    ASSESSMENT_STATUSES.sentry_message = this.sentry_prefix;
    ASSESSMENT_STATUSES.are_you_there = false;
    ASSESSMENT_STATUSES.incomplete_test = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;
    ASSESSMENT_STATUSES.assessment_successful_completion = false;
    ASSESSMENT_STATUSES.assessment_error = false;
    this.sentry_start_time = 0;
    this.are_you_there_modal_timer =
      config["are-you-there-timer-in-seconds"] * 1000;
    this.enabled_voice_listener = false;
    this.voice_command_received = false;
    this.shown_persistent_icon = false;

    // Remote NEXT: conditions skipped (reported as skipped_phases) and conditions measured to the end
    this.skipped_phases = [];
    this.completed_phase_count = 0;
    // Remote REWIND: the phase that most recently completed or was skipped, while it can still be
    // redone (until the next phase's start is accepted); null otherwise
    this.rewind_target = null;

    this.patient_feet_box_x = [];
    this.patient_feet_box_y = [];

    // Load shoe print image for calibration box
    this.shoe_print_img = new Image();
    this.shoe_print_img.src = shoePrintSrc;
    this.shoe_print_loaded = false;
    this.shoe_print_img.onload = () => {
      this.shoe_print_loaded = true;
    };

    // Sway arrays for each mCTSIB condition
    this.sway_open_eyes_x = [];
    this.sway_open_eyes_y = [];
    this.sway_closed_eyes_x = [];
    this.sway_closed_eyes_y = [];
    this.sway_open_eyes_foam_x = [];
    this.sway_open_eyes_foam_y = [];
    this.sway_closed_eyes_foam_x = [];
    this.sway_closed_eyes_foam_y = [];

    // Elapsed time tracking for each sub-assessment (in milliseconds)
    this.currentSubAssessmentStartTime = 0;
    this.openEyesElapsedMs = 0;
    this.closedEyesElapsedMs = 0;
    this.openEyesFoamElapsedMs = 0;
    this.closedEyesFoamElapsedMs = 0;

    // Reference frame tracking for sway measurement
    this.sway_reference_left = null;
    this.sway_reference_right = null;
    this.sway_frame_count = 0;
    this.ready_to_capture_reference = false;
    this.reference_captured = false;
    this.reference_frames_collected = null;
    this.reference_collection_count = 0;
    this.reference_collection_start_frame = 0;
    this.sway_x_data = [];
    this.sway_y_data = [];
    this.sway_smoothing_buffer = null;
    this.sway_ema_x = null;
    this.sway_ema_y = null;
    this.prev_raw_x = null;
    this.prev_raw_y = null;
    this.yawActive = null;

    // Torso quadrilateral reference metrics (for 4-keypoint sway detection)
    this.ref_left_hip = null;
    this.ref_right_hip = null;
    this.ref_shoulder_width = null;
    this.ref_hip_width = null;
    this.ref_left_edge = null;
    this.ref_right_edge = null;

    // Store the event listener reference for cleanup
    this.voiceCommandListener = (event) => {
      const command = event.detail.command;

      // Example: Trigger something based on the command
      if (command === "START") {
        console.log("Voice Triggered START command");
        this.voice_command_received = true;
        this.enabled_voice_listener = false;
        stopiOSVoiceListening();
      }
    };

    window.addEventListener("voice-command", this.voiceCommandListener);

    // Remote UP/DOWN patient selection: the participant the therapist selects before each
    // condition. The selection is kept for the later conditions.
    this.patient_selection = new PatientSelection();
  }

  /**
   * Cleanup voice listeners and reset states
   */
  cleanupVoiceListeners() {
    // Stop voice listening on any termination condition
    if (this.enabled_voice_listener) {
      console.log("🔊 Stopping voice listening due to assessment termination");
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
  }

  /**
   * Complete cleanup including event listener removal
   */
  destroy() {
    this.cleanupVoiceListeners();
    if (this.voiceCommandListener) {
      window.removeEventListener("voice-command", this.voiceCommandListener);
      this.voiceCommandListener = null;
    }
  }

  /**
   * Main update loop called on each video frame. Manages state transitions,
   * UI interactions, and calls to specific assessment logic.
   * @param {Array} poses - Array of detected poses from MoveNet.
   */
  update(poses) {
    this.current_time = Date.now();

    // The participant the therapist selected with the remote, while it can still be changed.
    // patient_id also makes movenet draw this person's skeleton green, and detect_patient prefers
    // this person (by id). Without a selection patient_id is left as it was.
    if (this.is_patient_selection_open()) {
      const patient = this.patient_selection.update(poses);
      if (patient) this.patient_id = patient.id;
    }

    // Handle restart, skip, and resume features
    // this.restart_sub_assessment_feature();
    this.restart_phase_feature();
    this.skip_phase_feature();

    // --------------------------- TERMINATION CONDITIONS --------------------------------
    if (
      this.current_state === mCTSIBAssessmentStates.test_complete ||
      ASSESSMENT_STATUSES.calibration_error_state === true ||
      this.current_state === mCTSIBAssessmentStates.assessment_interrupted ||
      ASSESSMENT_STATUSES.exit_assessment_modal === true
    ) {
      // Stop voice listening on any termination condition
      this.cleanupVoiceListeners();
      if (ASSESSMENT_STATUSES.calibration_error_state) {
        this.calibration_error_occured = true;
      }
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
      return;
    }

    // Handle inactivity prompt ("Are you there?")
    if (!poses || poses.length === 0) {
      if (this.are_you_there_flag) {
        this.are_you_there_flag = false;
        this.are_you_there_timer = Date.now();
      }
      const elapsed = this.current_time - this.are_you_there_timer;
      if (elapsed >= 60000 && !this.inactive_for_60_seconds) {
        this.inactive_for_60_seconds = true;
        this.append_sentry_message(
          "No pose/activity detected for more than 60 seconds."
        );
      }
      if (elapsed >= this.are_you_there_modal_timer) {
        this.are_you_there_flag = true;
        this.are_you_there_timer = 0;
        document.getElementById("hdnAreYouTherePopup").click();
      }
    } else {
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
    }

    if (document.readyState !== "complete") return;

    if (this.first_frame) {
      // --------------------------- INITIAL CALIBRATION --------------------------------

      sessionStorage.setItem("phaseNo", "1");
      sessionStorage.setItem(
        "mCTSIBAssessmentIndex",
        mCTSIB_ASSESSMENT.Open_Eyes
      );
      this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Open_Eyes;
      // Use shoulder sway tracking as assessment function
      this.assessment_function = (poses) => recordShoulderSway(this, poses);
      this.first_frame = false;
      //   this.audio_player.play(
      //     mCTSIBAssessmentStates.begin_assessment,
      //     this.current_arom_sub_assessment
      //   );
      // document.getElementById("hdnAssessmentStartUpMessage").click();
      this.camera.flip_horizontal();

      // Log initial DL model load status and screen resolution
      if (DL_MODELS.MessageForSentry === "") {
        this.append_sentry_message("DL_Model already loaded");
      } else {
        this.append_sentry_message(DL_MODELS.MessageForSentry);
        DL_MODELS.MessageForSentry = "";
      }
      this.append_sentry_message(
        "Screen Resolution: " +
          this.sit_stand_processing.canvas.width +
          "x" +
          this.sit_stand_processing.canvas.height
      );
      // this.append_sentry_message(
      //   "'Place chair around 8 feet away from phone' modal is displayed"
      // );
      this.first_frame_time = this.current_time;
    }

    if (this.track_patient) {
      // this.assessment_error_check(poses);
    }

    try {
      switch (this.current_state) {
        // --------------------------- 1) ASSESSMENT STARTED ------------------------------

        case mCTSIBAssessmentStates.PathwayCalibration:
          let elapsed_time0 = Date.now() - this.two_second_start_time;
          this.pathwayBlocked = true;
          if (elapsed_time0 <= 3500) return;
          this.current_state = mCTSIBAssessmentStates.calculatingDepth;
          this.two_second_start_time = Date.now();
          break;

        case mCTSIBAssessmentStates.calculatingDepth:
          // Once depth data is received AND api call is still valid, skip path checking
          if (this.calibration.depthData && this.is_api_called) {
            if (this.pathwayCalibrated) {
              this.pathwayCalibrated = false;
              setTimeout(() => {
                this.audio_player.play(mCTSIBAssessmentStates.calibrated);
                document.getElementById("hdnGaitCalibrationComplete")?.click();
                this.is_depth_calculated = true;

                setTimeout(() => {
                  this.current_state = mCTSIBAssessmentStates.begin_assessment;
                }, 2000);
              }, 3000);
            }
            break;
          }

          // Check if the path is clear
          this.is_path_clear = this.path_checking(poses);

          this.calibration.on_complete_callback();
          this.get_start_extended_points();
          if (!this.is_api_called && this.is_path_clear) {
            if (this.audio_player.is_available()) {
              this.audio_player.play(mCTSIBAssessmentStates.calculatingDepth);
            }
            this.uploadImageToS3AndCallAPI();
            this.is_api_called = true;
          }
          this.draw_PathClearanceBox();

          if (
            this.pathwayCalibrated &&
            this.is_path_clear &&
            !ASSESSMENT_STATUSES.assessment_Completed
          ) {
            const event = new CustomEvent("dontwalkmodal", {
              detail: this.calibration.percentDepthData,
            });
            document.getElementById("hdnGaitDontWalk").dispatchEvent(event);
          }

          // Path not clear -> show pathway blocked modal and retry indefinitely
          if (!this.is_path_clear && !this.calibration.depthData && this.pathwayBlocked) {
            console.warn("Pathway blocked GaitPathwayBlocked");
            this.pathwayBlocked = false;
            this.is_api_called = null; // Reset to restart calibration from 0%
            if (this.calibration) {
              this.calibration.percentDepthData = 0; // Reset progress bar
              this.calibration.depthData = null; // Reset depth data so path checking runs again
            }
            document.getElementById("hdnRemoveGaitDontWalk")?.click();
            document.getElementById("hdnGaitPathwayBlocked").click();
            if (this.audio_player.is_available()) {
              this.audio_player.play(mCTSIBAssessmentStates.pathwayBlocked);
            }
            this.current_state = mCTSIBAssessmentStates.PathwayCalibration;
            this.two_second_start_time = Date.now();
          }

          break;

        // case mCTSIBAssessmentStates.MoveInsidebox:

        // --------------------------- 1) BEGIN ASSESSMENT --------------------------------
        case mCTSIBAssessmentStates.begin_assessment:
          // this.sit_stand_processing.draw_patient_box();
          this.draw_PatientFeetBox(poses);
          this.pathwayBlocked = true;
          this.pathwayCalibrated = true;

          // Buffer variable -> 30% above & below the box's middle point
          let patient_inner_buffer = Math.abs(
            (this.patient_feet_box_y[0] - this.patient_feet_box_y[1]) * 0.2
          );

          // Check if someone is in the box (with a remote selection, the selected person)
          if (
            this.audio_player.is_available() &&
            MultiPoseUtility.are_both_ankles_in_box(
              this.patient_selection.start_check_poses(poses),
              this.patient_feet_box_x,
              [
                this.patient_feet_box_y[0] + patient_inner_buffer,
                this.patient_feet_box_y[1] - patient_inner_buffer,
              ],
              this.camera
            )
          ) {
            this.patient_in_box_counter++;
            if (this.patient_in_box_counter >= 5) {
              this.gesture_detector._screen_color_change();
              if (!this.patient_within_box_check) {
                this.patient_within_box_check = true;
                this.patient_within_box_timer = this.current_time;
              }
              if (this.current_time - this.patient_within_box_timer >= 3000) {
                this.within_bound_start_flag = true;
              }
            }
          } else {
            this.patient_in_box_counter = 0;
            this.within_bound_start_flag = false;
            this.patient_within_box_check = false;
            this.patient_within_box_timer = this.current_time;
            return;
          }
          if (this.within_bound_start_flag) {
            document.getElementById("hdnParticiantWithinBox").click();
            this.append_sentry_message(
              "Participant is within the Bounding box"
            );
            this.within_bound_start_flag = false;
            this.assessment_timer = this.current_time;
            this.current_state = mCTSIBAssessmentStates.calibration_1;
            return;
          }
          break;

        // --------------------------- 3) WITHIN BOUNDS --------------------------------
        case mCTSIBAssessmentStates.with_in_bounds:
          if (
            this.are_within_bbox(poses, this.sit_stand_processing.patient_box)
          ) {
            if (this.current_time - this.assessment_timer > 3000) {
              this.gesture_detector.detect_start_gesture(poses);
              if (
                !this.enabled_voice_listener &&
                !this.voice_command_received &&
                this.audio_player.is_available()
              ) {
                console.log("🔊 Start of voice listening...");
                setTimeout(() => {
                  startiOSVoiceListening();
                }, 100);
                this.enabled_voice_listener = true;
              }
              if (
                this.gesture_detector.start_gesture_issued() ||
                this.voice_command_received
              ) {
                if (this.enabled_voice_listener) {
                  console.log("🔊 Stopping voice listening...");
                  stopiOSVoiceListening();
                  this.enabled_voice_listener = false;
                }
                this.assessment_timer = this.current_time;
                this.current_state = mCTSIBAssessmentStates.calibration_1;
                this.start_time = this.current_time;
                this.therapist_id = this.gesture_detector.gesture_issued_by;
                this.gesture_detector.reset();
                document.getElementById("hdnCalibrationMessage").click();
                // this.audio_player.play(
                //   mCTSIBAssessmentStates.calibrated,
                //   this.current_arom_sub_assessment
                // );
                this.append_sentry_message("Calibration Completed.");
                this.append_sentry_message(
                  "Number of persons in the frame while gesture detection: " +
                    poses.length
                );
                this.assessment_timer = this.current_time;
                this.voice_command_received = false;
              }
            }
          } else {
            this.sit_stand_processing.draw_patient_box();
          }
          break;

        // --------------------------- 4) CALIBRATION PHASE 1 --------------------------------
        case mCTSIBAssessmentStates.calibration_1:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 3000 &&
            !this.display_modalFaceCamera
          ) {
            this.display_modalFaceCamera = true;
            document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
            this.audio_player.play(
              mCTSIBAssessmentStates.calibration_1,
              this.current_arom_sub_assessment
            );
            this.assessment_timer = this.current_time;
          } else if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 5000 &&
            this.display_modalFaceCamera
          ) {
            this.display_modalFaceCamera = false;
            document
              .getElementById("hdnFaceCameraPalmForwardPhase1close")
              .click();
            this.current_state = mCTSIBAssessmentStates.raise_hand;
            this.assessment_timer = this.current_time;
            if (this.detect_patient(poses)) {
              this.track_patient = true;
            }
          }
          break;

        // --------------------------- 7) CALIBRATION PHASE 3 : Raise Hand --------------------------------
        case mCTSIBAssessmentStates.raise_hand:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer > 1500
          ) {
            document.getElementById("hdnAromTherapistReadyMessage").click();
            this.audio_player.play(
              mCTSIBAssessmentStates.raise_hand,
              this.current_arom_sub_assessment
            );
            this.current_state = mCTSIBAssessmentStates.gesture_detection;
            this.assessment_timer = this.current_time;
          }
          break;

        // --------------------------- 8) CALIBRATION PHASE 4 : Gesture Detection --------------------------------
        case mCTSIBAssessmentStates.gesture_detection:
          // this.sit_stand_processing.draw_patient_box();
          this.draw_PatientFeetBox(poses);
          // With a remote selection, only the selected person counts; otherwise anyone in the box
          let patient_in_box = MultiPoseUtility.are_both_ankles_in_box(
            this.patient_selection.start_check_poses(poses),
            this.patient_feet_box_x,
            this.patient_feet_box_y,
            this.camera
          );
          // Show persistent icon once audio finishes (algorithm-driven, not gated by bounding box)
          if (!this.shown_persistent_icon && this.audio_player.is_available()) {
            this.shown_persistent_icon = true;
            document.getElementById("hdnRaiseArmStopModalPopup")?.click();
          }

          if (this.handFlag) {
            if (patient_in_box) {
              this.gesture_detector._screen_color_change();

              this.gesture_detector.detect_start_gesture(poses);

              if (
                !this.enabled_voice_listener &&
                !this.voice_command_received &&
                this.audio_player.is_available()
              ) {
                console.log("🔊 Start of voice listening...");
                setTimeout(() => {
                  startiOSVoiceListening();
                }, 100);
                this.enabled_voice_listener = true;
              }
              if (
                this.gesture_detector.start_gesture_issued() ||
                this.voice_command_received
              ) {
                this.shown_persistent_icon = true;
                document.getElementById("hdnRaiseArmStopModalPopup").click();
                document.getElementById("hdnHideHandRaisePersistentIcon")?.click();
                this.handFlag = false;
                this.rewind_target = null; // started: REWIND can no longer go back

                if (!this.audio_player.is_available()) this.audio_player.stop();
                if (this.enabled_voice_listener) {
                  console.log("🔊 Stopping voice listening...");
                  stopiOSVoiceListening();
                  this.enabled_voice_listener = false;
                }

                this.calibration.start_point =
                  this.calibration.get_warped_point(
                    this.calibration.start_point_cv
                  );
                // this.calibration.stop_point = this.calibration.get_warped_point(
                //   this.calibration.stop_point_cv
                // );
                this.reset_pose_estimator();
                this.switch_to_blazepose(poses);
                this.therapist_id = this.gesture_detector.gesture_issued_by;
                this.gesture_detector.reset();
                this.append_sentry_message("Calibration Completed.");
                this.append_sentry_message(
                  "Number of persons in the frame while gesture detection: " +
                    poses.length
                );
                document.getElementById("hdnAromPhase1StartMessage").click();
                this.audio_player.play(
                  mCTSIBAssessmentStates.gesture_detection,
                  this.current_arom_sub_assessment
                );
                this.voice_command_received = false;
                setTimeout(() => {
                  // Not if the condition was restarted meanwhile (remote stop + REWIND resets handFlag)
                  if (this.handFlag) return;
                  this.current_state = mCTSIBAssessmentStates.pre_assessment;
                }, 2500);
                this.assessment_timer = this.current_time;
              }
            } else {
              if (this.enabled_voice_listener) {
                console.log("🔊 Stopping voice listening...");
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }
              // this.enabled_voice_listener = false;
            }
          }
          break;

        // --------------------------- 9) PRE-ASSESSMENT --------------------------------
        case mCTSIBAssessmentStates.pre_assessment:
          this.handFlag = true;
          if (this.initialize_calibration_error_occured) {
            this.calibration_error_occured = false;
            this.initialize_calibration_error_occured = false;
          }
          if (poses.length > 1) break;
          if (this.audio_player.is_available()) {
            this.assessment_timer = this.current_time;
            AromMenuOptions.actualAssessmentStartTime = this.current_time;
            // Capture start time for this sub-assessment
            this.currentSubAssessmentStartTime = Date.now();
            if (!this.start_time) {
              this.start_time = Date.now();
            }
            this.current_state = mCTSIBAssessmentStates.assessment_In_progress;
            document.getElementById("hdnAromPhase1StartMessageClose").click();
            document.getElementById("btn-arom-assessment-counter").click();
          }
          // Reset reference frame state for new assessment
          // Reference will be captured immediately when entering assessment_In_progress state
          this.sway_reference_left = null;
          this.sway_reference_right = null;
          this.sway_frame_count = 0;
          this.reference_captured = false;
          this.reference_frames_collected = null;
          this.reference_collection_count = 0;
          this.reference_collection_start_frame = 0;
          this.ready_to_capture_reference = true; // Set flag to capture reference immediately in next frame
          this.sway_x_data = [];
          this.sway_y_data = [];
          this.sway_smoothing_buffer = null;
          this.sway_ema_x = null;
          this.sway_ema_y = null;
          this.prev_raw_x = null;
          this.prev_raw_y = null;
          this.yawActive = null;
          // Reset torso quadrilateral reference metrics
          this.ref_left_hip = null;
          this.ref_right_hip = null;
          this.ref_shoulder_width = null;
          this.ref_hip_width = null;
          this.ref_left_edge = null;
          this.ref_right_edge = null;

          const patientHeelDistance = this.calibration.get_depth_distance(
            poses[0].keypoints[32].x,
            poses[0].keypoints[32].y
          );
          console.log("patientHeelDistance", patientHeelDistance);

          distace.patientHeelToIpad = patientHeelDistance;

          break;

        // --------------------------- 10) ASSESSMENT IN PROGRESS --------------------------------
        case mCTSIBAssessmentStates.assessment_In_progress:
          this.initialize_calibration_error_occured = true;
          document.getElementById("output")?.classList.add("mctsib-cam");

          // Calculate and log sway data for live feedback
          // Reference frame will be captured immediately in the first few frames (averages 5 frames)
          const swayResult = this.assessment_function(poses);

          if (swayResult && typeof swayResult === "object") {
            // Values are in FEET
            const { sway_sideways_ft, sway_forward_backward_ft } = swayResult;
            // Store sway values in the correct array for the current sub-assessment, only if valid
            console.log("Sway Result (feet):", swayResult);
            if (
              Number.isFinite(sway_sideways_ft) &&
              Number.isFinite(sway_forward_backward_ft)
            ) {
              // Check if sway exceeds the measuring circle boundary (6 inches)
              const swayDistInches = Math.sqrt(
                sway_sideways_ft * sway_sideways_ft +
                sway_forward_backward_ft * sway_forward_backward_ft
              ) * 12; // feet to inches

              if (swayDistInches > MAX_SWAY_INCHES) {
                // Patient exceeded boundary — trigger error modal (Discard & Retry)
                ASSESSMENT_STATUSES.assessment_error = true; // Set FIRST per established pattern
                this.cleanupVoiceListeners();
                document.getElementById("output")?.classList.remove("mctsib-cam");
                this.current_state = mCTSIBAssessmentStates.assessment_interrupted;
                this.append_sentry_message(
                  "mCTSIB sway boundary exceeded (" + swayDistInches.toFixed(2) + " inches) during " +
                  this.current_arom_sub_assessment.toString() + " — showing error modal"
                );
                document.getElementById("hdnAssessementErrorPopup")?.click();
                return;
              }

              switch (this.current_arom_sub_assessment) {
                case mCTSIB_ASSESSMENT.Open_Eyes:
                  this.sway_open_eyes_x.push(sway_sideways_ft);
                  this.sway_open_eyes_y.push(sway_forward_backward_ft);
                  break;
                case mCTSIB_ASSESSMENT.Closed_Eyes:
                  this.sway_closed_eyes_x.push(sway_sideways_ft);
                  this.sway_closed_eyes_y.push(sway_forward_backward_ft);
                  break;
                case mCTSIB_ASSESSMENT.Foam_Open_Eyes:
                  this.sway_open_eyes_foam_x.push(sway_sideways_ft);
                  this.sway_open_eyes_foam_y.push(sway_forward_backward_ft);
                  break;
                case mCTSIB_ASSESSMENT.Foam_Closed_Eyes:
                  this.sway_closed_eyes_foam_x.push(sway_sideways_ft);
                  this.sway_closed_eyes_foam_y.push(sway_forward_backward_ft);
                  break;
              }
            }
          }
          let time_left =
            TIME_LIMIT_SEC -
            Math.floor((this.current_time - this.assessment_timer) / 1000);
          if (time_left <= 3 && this.play_beep_sound) {
            this.play_beep_sound = false;
            this.audio_player.play(
              mCTSIBAssessmentStates.assessment_In_progress,
              this.current_arom_sub_assessment
            );
          }
          if (
            this.current_time - this.assessment_timer >=
            TIME_LIMIT_SEC * 1000
          ) {
            document.getElementById("output")?.classList.remove("mctsib-cam");

            // Capture end time and store elapsed time for current sub-assessment
            const elapsedMs = Date.now() - this.currentSubAssessmentStartTime;
            switch (this.current_arom_sub_assessment) {
              case mCTSIB_ASSESSMENT.Open_Eyes:
                this.openEyesElapsedMs = elapsedMs;
                break;
              case mCTSIB_ASSESSMENT.Closed_Eyes:
                this.closedEyesElapsedMs = elapsedMs;
                break;
              case mCTSIB_ASSESSMENT.Foam_Open_Eyes:
                this.openEyesFoamElapsedMs = elapsedMs;
                break;
              case mCTSIB_ASSESSMENT.Foam_Closed_Eyes:
                this.closedEyesFoamElapsedMs = elapsedMs;
                break;
            }

            this.assessment_timer = this.current_time;
            this.completed_phase_count++;
            this.rewind_target = this.current_arom_sub_assessment;
            this.current_state = mCTSIBAssessmentStates.post_assessment_modal;
            document.getElementById("hdnAromCompletionMessage").click();
            document.getElementById("btn-arom-assessment-counter").click();
            this.audio_player.play(
              mCTSIBAssessmentStates.post_assessment_modal,
              this.current_arom_sub_assessment
            );
          }

          break;

        // --------------------------- 11) POST-ASSESSMENT --------------------------------
        case mCTSIBAssessmentStates.post_assessment_modal:
          if (
            this.audio_player.is_available() &&
            this.current_time - this.assessment_timer >= 3000
          ) {
            this.assessment_timer = this.current_time;
            this.play_beep_sound = true;
            this.switch_to_next_sub_assessment_state();
          }
          break;

        // --------------------------- 12) ALL SUB-ASSESSMENTS COMPLETE --------------------------------
        case mCTSIBAssessmentStates.all_sub_assessments_complete:
          this.end_time = this.current_time;
          let response = this.build_response();
          this.append_sentry_message(
            "Assessment response:" + JSON.stringify(response)
          );
          if (window["AssessmentPageReference"]) {
            window["AssessmentPageReference"].CompleteAssessment(response);
          } else {
            console.log("window['AssessmentPageReference'] not found");
          }
          // Stop voice listening on successful completion
          this.cleanupVoiceListeners();
          this.current_state = mCTSIBAssessmentStates.test_complete;
          ASSESSMENT_STATUSES.assessment_Completed = true;
          break;

        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Checks if any detected poses have feet within the given bounding box.
   * @param {Array} poses - Array of detected poses.
   * @param {Object} bounding_box - Bounding box with x, y, width, height.
   * @returns {boolean} - True if at least one pose is within the box.
   */
  are_within_bbox(poses, bounding_box) {
    let offset = 0;
    if (!this.sit_stand_processing.is_patient_in_box_status) {
      offset = bounding_box.height;
    }
    for (let pose of poses) {
      let leftAnkle = this.camera.scale_to_fit({
        x: pose.keypoints[15].x,
        y: pose.keypoints[15].y,
      });
      let rightAnkle = this.camera.scale_to_fit({
        x: pose.keypoints[16].x,
        y: pose.keypoints[16].y,
      });
      if (
        rightAnkle.x > bounding_box.x &&
        leftAnkle.y > bounding_box.y + Math.abs(offset * 0.25) &&
        leftAnkle.x < bounding_box.x + bounding_box.width &&
        rightAnkle.y <
          bounding_box.y + bounding_box.height - Math.abs(offset * 0.25)
      ) {
        this.sit_stand_processing.is_patient_in_box_status = true;
        return true;
      }
    }
    this.sit_stand_processing.is_patient_in_box_status = false;
    return false;
  }

  /**
   * Builds the final assessment response object with summary metrics.
   * @returns {Object} - Assessment result including start/end times, sway data, and scores.
   */
  build_response() {
    // Helper to filter out undefined or non-finite values
    const filterValid = (arr) =>
      Array.isArray(arr) ? arr.filter((v) => Number.isFinite(v)) : [];

    // Filter sway arrays
    const swayOpenEyesX = filterValid(this.sway_open_eyes_x);
    const swayOpenEyesY = filterValid(this.sway_open_eyes_y);
    const swayClosedEyesX = filterValid(this.sway_closed_eyes_x);
    const swayClosedEyesY = filterValid(this.sway_closed_eyes_y);
    const swayOpenEyesFoamX = filterValid(this.sway_open_eyes_foam_x);
    const swayOpenEyesFoamY = filterValid(this.sway_open_eyes_foam_y);
    const swayClosedEyesFoamX = filterValid(this.sway_closed_eyes_foam_x);
    const swayClosedEyesFoamY = filterValid(this.sway_closed_eyes_foam_y);

    // Calculate raw metrics directly (in feet) to avoid precision loss from formatMetrics
    const rawMetrics = [
      calculateSubAssessmentMetrics(swayOpenEyesX, swayOpenEyesY, this.openEyesElapsedMs),
      calculateSubAssessmentMetrics(swayClosedEyesX, swayClosedEyesY, this.closedEyesElapsedMs),
      calculateSubAssessmentMetrics(swayOpenEyesFoamX, swayOpenEyesFoamY, this.openEyesFoamElapsedMs),
      calculateSubAssessmentMetrics(swayClosedEyesFoamX, swayClosedEyesFoamY, this.closedEyesFoamElapsedMs),
    ];

    const elapsedMsValues = [
      this.openEyesElapsedMs,
      this.closedEyesElapsedMs,
      this.openEyesFoamElapsedMs,
      this.closedEyesFoamElapsedMs,
    ];

    const FEET_TO_METERS = 0.3048;

    const modeCodes = [
      "eyes_open_standing_on_floor",
      "eyes_closed_standing_on_floor",
      "eyes_open_standing_on_foam",
      "eyes_closed_standing_on_foam",
    ];

    // Format datetime to match XML: "YYYY-MM-DD HH:mm:ss.SSS"
    const formatDateTime = (timestamp) => {
      const d = new Date(timestamp);
      const pad = (n, len = 2) => n.toString().padStart(len, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
    };

    // Build phases array matching XML structure
    const phases = rawMetrics.map((metrics, index) => {
      const phaseScore = calculatePhaseScore(metrics);
      return {
        plugin: "VSTBalanceMobile",
        activity_id: "PhaseComplete",
        test_type: "ctsib",
        skeletons_csv: "",
        elapsed_time: elapsedMsValues[index],
        cumulative_sway_distance: metrics ? metrics.cumulativeSway * FEET_TO_METERS : 0,
        mean_displacement_from_start: metrics ? metrics.meanDistFromStart * FEET_TO_METERS : 0,
        sway_range_x: metrics ? metrics.rangeX * FEET_TO_METERS : 0,
        sway_range_y: metrics ? metrics.rangeY * FEET_TO_METERS : 0,
        sway_range_z: 0,
        phase_score: phaseScore,
        mode: {
          index: index,
          code: modeCodes[index],
        },
        start_time: formatDateTime(this.start_time),
        end_time: formatDateTime(this.end_time),
      };
    });

    return {
      phases: phases,
      // Conditions skipped with the remote's NEXT (mCTSIB_ASSESSMENT values); their metrics are 0
      skipped_phases: this.skipped_phases,
    };

    // COMMENTED OUT: Previous response formats kept for future testing
    /*
    // --- Format 1: Summary table (for 3D vs 2D accuracy comparison) ---
    const scoreResults = calculateAllScores(
      {
        openEyesX: swayOpenEyesX, openEyesY: swayOpenEyesY,
        closedEyesX: swayClosedEyesX, closedEyesY: swayClosedEyesY,
        openEyesFoamX: swayOpenEyesFoamX, openEyesFoamY: swayOpenEyesFoamY,
        closedEyesFoamX: swayClosedEyesFoamX, closedEyesFoamY: swayClosedEyesFoamY,
      },
      {
        openEyes: this.openEyesElapsedMs, closedEyes: this.closedEyesElapsedMs,
        openEyesFoam: this.openEyesFoamElapsedMs, closedEyesFoam: this.closedEyesFoamElapsedMs,
      }
    );
    const m = scoreResults.metrics;
    const p = scoreResults.phase_scores;
    const s = scoreResults.sensory_scores;
    return {
      test_type: "mCTSIB",
      start_time: this.start_time.toString(),
      end_time: this.end_time.toString(),
      summary_table: [
        { trial: 1, measure: "Eyes open floor", sway_feet: m.open_eyes_floor?.cumulative_sway_feet ?? null, shift_feet: m.open_eyes_floor?.mean_dist_from_start_feet ?? null, time_seconds: m.open_eyes_floor?.elapsed_sec ?? null, score: p.open_eyes_floor },
        { trial: 1, measure: "Eyes closed floor", sway_feet: m.closed_eyes_floor?.cumulative_sway_feet ?? null, shift_feet: m.closed_eyes_floor?.mean_dist_from_start_feet ?? null, time_seconds: m.closed_eyes_floor?.elapsed_sec ?? null, score: p.closed_eyes_floor },
        { trial: 1, measure: "Eyes open foam", sway_feet: m.open_eyes_foam?.cumulative_sway_feet ?? null, shift_feet: m.open_eyes_foam?.mean_dist_from_start_feet ?? null, time_seconds: m.open_eyes_foam?.elapsed_sec ?? null, score: p.open_eyes_foam },
        { trial: 1, measure: "Eyes closed foam", sway_feet: m.closed_eyes_foam?.cumulative_sway_feet ?? null, shift_feet: m.closed_eyes_foam?.mean_dist_from_start_feet ?? null, time_seconds: m.closed_eyes_foam?.elapsed_sec ?? null, score: p.closed_eyes_foam },
      ],
      sensory_scores: { visual: s.visual, somatosensory: s.somatosensory, vestibular: s.vestibular, composite: s.overall },
    };

    // --- Format 2: Full response with raw sway arrays ---
    return {
      test_type: "mCTSIB",
      start_time: this.start_time.toString(),
      end_time: this.end_time.toString(),
      sway_open_eyes_x: swayOpenEyesX, sway_open_eyes_y: swayOpenEyesY,
      sway_closed_eyes_x: swayClosedEyesX, sway_closed_eyes_y: swayClosedEyesY,
      sway_open_eyes_foam_x: swayOpenEyesFoamX, sway_open_eyes_foam_y: swayOpenEyesFoamY,
      sway_closed_eyes_foam_x: swayClosedEyesFoamX, sway_closed_eyes_foam_y: swayClosedEyesFoamY,
      phase_scores: scoreResults.phase_scores,
      sensory_scores: scoreResults.sensory_scores,
      metrics: scoreResults.metrics,
    };
    */
  }

  /**
   * Identifies the patient pose from multi-person detections.
   * Prefers already-tracked ID or chooses closest feet within patient box.
   * @param {Array} poses - Array of detected poses.
   * @returns {MoveNetPoseWrapper|null} - Wrapped pose of patient or null.
   */
  detect_patient(poses) {
    if (this.patient_id !== -1) {
      for (let person of poses) {
        if (person.score > 0.25 && person.id === this.patient_id) {
          this.patient_ID_changed = false;
          return new MoveNetPoseWrapper(person);
        }
      }
    }
    if (!poses || poses.length === 0) return null;

    let patient_ID = null;
    let therapist_pose = null;
    let min_feet_to_center_distance = Infinity;

    for (let person of poses) {
      if (person.id === this.therapist_id) therapist_pose = person;
      if (person.score > 0.25 && person.id !== this.therapist_id) {
        let left_ankle = this.camera.scale_to_fit({
          x: person.keypoints[15].x,
          y: person.keypoints[15].y,
        });
        let right_ankle = this.camera.scale_to_fit({
          x: person.keypoints[16].x,
          y: person.keypoints[16].y,
        });
        console.log("looping in the poses");
        if (
          right_ankle.x > this.sit_stand_processing.patient_box.x &&
          left_ankle.y > this.sit_stand_processing.patient_box.y &&
          left_ankle.x <
            this.sit_stand_processing.patient_box.x +
              this.sit_stand_processing.patient_box.width &&
          right_ankle.y <
            this.sit_stand_processing.patient_box.y +
              this.sit_stand_processing.patient_box.height
        ) {
          console.log("person within the box found");

          let current_feet_to_center_distance = Math.abs(
            (left_ankle.x + right_ankle.x) / 2 -
              (this.sit_stand_processing.patient_box.x +
                (this.sit_stand_processing.patient_box.x +
                  this.sit_stand_processing.patient_box.width)) /
                2
          );
          if (current_feet_to_center_distance < min_feet_to_center_distance) {
            console.log("person ID:", person.id);
            min_feet_to_center_distance = current_feet_to_center_distance;
            patient_ID = person;
            this.patient_id = person.id;
          }
        }
      }
    }
    if (patient_ID) {
      this.patient_ID_changed = true;
      return new MoveNetPoseWrapper(patient_ID);
    }
    if (therapist_pose) {
      let left_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[15].x,
        y: therapist_pose.keypoints[15].y,
      });
      let right_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[16].x,
        y: therapist_pose.keypoints[16].y,
      });
      if (
        right_ankle.x > this.sit_stand_processing.patient_box.x &&
        left_ankle.y > this.sit_stand_processing.patient_box.y &&
        left_ankle.x <
          this.sit_stand_processing.patient_box.x +
            this.sit_stand_processing.patient_box.width &&
        right_ankle.y <
          this.sit_stand_processing.patient_box.y +
            this.sit_stand_processing.patient_box.height
      ) {
        this.patient_ID_changed = true;
        this.patient_id = therapist_pose.id;
        return new MoveNetPoseWrapper(therapist_pose);
      }
    }
    return null;
  }

  switch_to_blazepose(poses) {
    // The therapist's selection wins over the automatic pick (it already passed the start-box check)
    let patient =
      this.patient_selection.selected_patient() ||
      MultiPoseUtility.return_patient(
        poses,
        this.patient_feet_box_x,
        [this.patient_feet_box_y[0], this.patient_feet_box_y[1]],
        this.gesture_detector.gesture_issued_by,
        this.camera
      );
    if (!patient) {
      console.log(
        "Patient not found in the box - return_patient returning Null"
      );
      return;
    } else {
      // Defensive: check keypoints exist
      const left_hip = patient.keypoints[11];
      const right_hip = patient.keypoints[12];
      if (!left_hip || !right_hip) {
        console.log("Hip keypoints missing in patient pose");
        return;
      }
      let seed_rect = this.get_blazepose_bbox(patient);
      this.patient_pose = patient;

      console.log(`Seed Rect : ${JSON.stringify(seed_rect)}`);
      this.switch_to_single_pose_callback(seed_rect);

      this.append_sentry_message(
        "Switching to blaze pose with seed rect: " + JSON.stringify(seed_rect)
      );
    }
  }

  /**
   * Computes the seed bounding box for BlazePose based on patient's hips and spine.
   * @param {MoveNetPoseWrapper} patient - Wrapped patient pose.
   * @returns {Object} - Seed rectangle normalized by video dimensions.
   */
  get_blazepose_bbox(patient) {
    let left_hip = patient.keypoints[11];
    let right_hip = patient.keypoints[12];
    let spine_base = {
      x: (left_hip.x + right_hip.x) / 2,
      y: (left_hip.y + right_hip.y) / 2,
    };
    let bbox = patient.box;
    let box_size = 2 * Math.ceil(bbox.yMax * this.options.sh - spine_base.y);
    this.append_sentry_message(
      "Seed box: " +
        JSON.stringify(bbox) +
        " spine base: " +
        JSON.stringify(spine_base) +
        " box size: " +
        box_size
    );
    return {
      xCenter: spine_base.x / this.camera.video.videoWidth,
      yCenter: spine_base.y / this.camera.video.videoHeight,
      width: box_size / this.camera.video.videoWidth,
      height: box_size / this.camera.video.videoHeight,
      rotation: 0.0,
    };
  }

  /**
   * True while the patient can be changed with the remote: before each condition, from the feet box
   * check (or the condition's instructions) until the start is accepted.
   * @returns {boolean}
   */
  is_patient_selection_open() {
    const states = mCTSIBAssessmentStates;
    return (
      ([states.begin_assessment, states.calibration_1, states.raise_hand].includes(
        this.current_state
      ) ||
        // handFlag is false once the start was accepted
        (this.current_state === states.gesture_detection && this.handFlag)) &&
      this.patient_feet_box_x.length >= 2 &&
      this.patient_feet_box_y.length >= 2 &&
      !this.voice_command_received &&
      !ASSESSMENT_STATUSES.exit_assessment_modal &&
      !ASSESSMENT_STATUSES.calibration_error_state &&
      !ASSESSMENT_STATUSES.assessment_error
    );
  }

  /**
   * Patient selection from outside the frame loop (BLE remote UP/DOWN): moves the patient to the
   * next detected person on screen. They still have to be in the feet box for the condition to start.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the selection changed.
   */
  cyclePatient(direction, source) {
    if (!this.is_patient_selection_open()) return false;

    const selected = this.patient_selection.cycle(direction);
    if (!selected) return false;

    this.patient_id = selected.id;
    this.append_sentry_message(`Patient selected by ${source}: pose id ${selected.id}`);
    return true;
  }

  /**
   * Start trigger from outside the frame loop (BLE remote START_STOP). Accepted in gesture
   * detection with the selected participant's ankles in the box, also while the prompt is still
   * playing (it is cut short). Starts the current condition.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStartTrigger(source) {
    // A remote start needs a participant selected with UP/DOWN (a hand raise or voice start doesn't)
    const patient = this.patient_selection.selected_patient();
    if (
      !patient ||
      this.current_state !== mCTSIBAssessmentStates.gesture_detection ||
      !this.handFlag ||
      this.voice_command_received ||
      !MultiPoseUtility.are_both_ankles_in_box(
        [patient],
        this.patient_feet_box_x,
        this.patient_feet_box_y,
        this.camera
      )
    ) {
      return false;
    }
    // Same flag as voice START, so update() starts the condition through the existing path
    this.voice_command_received = true;
    // Cut the prompt short: the remote may start while it is still playing
    this.audio_player.stop();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.append_sentry_message(`Start triggered by ${source}`);
    return true;
  }

  /**
   * Stop trigger from outside the frame loop (BLE remote START_STOP). Accepted only while a
   * condition is running (start triggered, condition not yet finished or errored). Freezes
   * update() so nothing more is measured while the caller shows the exit choice.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStopTrigger(source) {
    const running =
      // handFlag is false between the start and pre_assessment (set after a 2.5 s timeout)
      (this.current_state === mCTSIBAssessmentStates.gesture_detection &&
        !this.handFlag) ||
      this.current_state === mCTSIBAssessmentStates.pre_assessment ||
      this.current_state === mCTSIBAssessmentStates.assessment_In_progress;
    if (
      !running ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = true;
    this.append_sentry_message(`Assessment stopped by ${source}`);
    return true;
  }

  /**
   * NEXT trigger from outside the frame loop (BLE remote NEXT). Accepted only while no condition is
   * running:
   *   - during the setup calibration (depth calibration, waiting for the patient in the box): leave
   *     the assessment (caller exits without a modal)
   *   - during a condition's setup or raise-hand step, not the last condition: skip it and move to the next one
   *   - last condition, none measured yet: leave the assessment (caller exits without a modal)
   *   - last condition, some measured: skip it and complete, which shows the results
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {"next_phase"|"complete"|"exit"|null} What happened, or null if NEXT doesn't apply now.
   */
  receiveNextTrigger(source) {
    const calibrating = [
      mCTSIBAssessmentStates.PathwayCalibration,
      mCTSIBAssessmentStates.calculatingDepth,
      mCTSIBAssessmentStates.begin_assessment,
    ].includes(this.current_state);
    const between_conditions =
      [
        mCTSIBAssessmentStates.calibration_1,
        mCTSIBAssessmentStates.raise_hand,
      ].includes(this.current_state) ||
      // handFlag is false once the start was accepted (then it's running)
      (this.current_state === mCTSIBAssessmentStates.gesture_detection &&
        this.handFlag);
    if (
      !(calibrating || between_conditions) ||
      this.voice_command_received ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return null;
    }

    if (calibrating) {
      ASSESSMENT_STATUSES.exit_assessment_modal = true;
      this.append_sentry_message(`Assessment exited by ${source} (NEXT during calibration)`);
      return "exit";
    }

    const is_last_condition =
      this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Foam_Closed_Eyes;
    if (is_last_condition && this.completed_phase_count === 0) {
      ASSESSMENT_STATUSES.exit_assessment_modal = true;
      this.append_sentry_message(`Assessment exited by ${source} (NEXT, no condition measured)`);
      return "exit";
    }

    this.skip_current_phase(source);
    if (is_last_condition) {
      this.current_state = mCTSIBAssessmentStates.all_sub_assessments_complete;
      return "complete";
    }
    this.switch_to_next_sub_assessment_state();
    return "next_phase";
  }

  // Leaves the current condition without measuring it and records it as skipped
  skip_current_phase(source) {
    this.skipped_phases.push(this.current_arom_sub_assessment);
    this.rewind_target = this.current_arom_sub_assessment;
    // Close this phase's prompts before the transition opens the next phase's own
    document.getElementById("hdnClosePhasePrompts")?.click();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.audio_player.stop();
    this.append_sentry_message(
      `Phase ${this.current_arom_sub_assessment} skipped by ${source}`
    );
  }

  /**
   * REWIND trigger from outside the frame loop (BLE remote REWIND). Goes back exactly one condition:
   * redoes the condition that most recently completed or was skipped with NEXT. Active from then until
   * the next condition's start is accepted, and on the assessment-complete modal after the last condition
   * (the caller closes the modal). After a rewind it stays inactive until a condition completes or is
   * skipped again, so it never goes back more than one condition.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @param {boolean} complete_modal_open - Whether the assessment-complete modal is showing.
   * @returns {boolean} True if it rewound.
   */
  receiveRewindTrigger(source, complete_modal_open = false) {
    if (
      this.rewind_target === null ||
      this.voice_command_received ||
      ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.calibration_error_state ||
      ASSESSMENT_STATUSES.assessment_error
    ) {
      return false;
    }
    const from_complete =
      this.current_state === mCTSIBAssessmentStates.test_complete;
    const between_conditions =
      [
        mCTSIBAssessmentStates.post_assessment_modal,
        mCTSIBAssessmentStates.calibration_1,
        mCTSIBAssessmentStates.raise_hand,
      ].includes(this.current_state) ||
      // handFlag is false once the start was accepted (then it's running)
      (this.current_state === mCTSIBAssessmentStates.gesture_detection &&
        this.handFlag);
    if (from_complete ? !complete_modal_open : !between_conditions) return false;

    this.rewind_to_phase(this.rewind_target, source);
    return true;
  }

  // Makes `phase` (the one that completed or was skipped) the current condition again and restarts it
  rewind_to_phase(phase, source) {
    if (this.skipped_phases.includes(phase)) {
      this.skipped_phases = this.skipped_phases.filter((p) => p !== phase);
    } else {
      this.completed_phase_count--;
    }
    this.rewind_target = null;

    this.restart_phase(phase, source);
  }

  /**
   * REWIND on the "Incomplete Test" choice after a stop (BLE remote): restarts the condition that was
   * stopped, from its calibration_1 prompt, keeping the earlier conditions' results. That condition was neither completed nor
   * skipped, so the counts don't change.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if it restarted (only while a stop has the assessment frozen).
   */
  rewindStoppedPhase(source) {
    if (
      !ASSESSMENT_STATUSES.exit_assessment_modal ||
      ASSESSMENT_STATUSES.assessment_error ||
      this.current_state === mCTSIBAssessmentStates.test_complete
    ) {
      return false;
    }
    ASSESSMENT_STATUSES.exit_assessment_modal = false; // unfreeze update()
    this.restart_phase(this.current_arom_sub_assessment, `${source} after a stop`);
    return true;
  }

  /**
   * REWIND on the Assessment Error modal (BLE remote): restarts the condition that errored, from its first
   * instruction, keeping the earlier conditions' results, like the modal's Retry. Only for an error inside a
   * condition (assessment_error is set); a setup failure such as the depth API leaves it unset, and the
   * caller restarts the whole assessment instead.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if it restarted.
   */
  rewindErroredPhase(source) {
    if (!ASSESSMENT_STATUSES.assessment_error) return false;
    this.restart_phase(this.current_arom_sub_assessment, `${source} after an error`);
    return true;
  }

  // Makes `phase` the current condition and restarts it from its calibration_1 prompt
  restart_phase(phase, source) {
    document.getElementById("hdnClosePhasePrompts")?.click();
    this.current_arom_sub_assessment = phase;
    // Same reset as the error modal's Retry: stops audio and voice, back to MoveNet and
    // calibration_1, this condition's sway data cleared and phaseNo set
    this.retryCurrentPhase();
    this.append_sentry_message(`Rewound to ${phase} by ${source}`);
  }

  /**
   * Appends timestamped messages to the Sentry log stored in ASSESSMENT_STATUSES.
   * @param {string} message - Message to log.
   */
  append_sentry_message(message) {
    let event = new Date();
    ASSESSMENT_STATUSES.sentry_message += `\n${event.toISOString()}: ${message}`;
    if (message === "Raise your hand to Calibrate.") {
      this.sentry_start_time = Date.now();
      setTimeout(() => {
        if (this.current_state === mCTSIBAssessmentStates.with_in_bounds) {
          ASSESSMENT_STATUSES.sentry_message +=
            "\nGesture (Raise Hand) not detected for 30 Seconds";
        }
      }, 30000);
    } else if (message === "Calibration Completed.") {
      let diff = (Date.now() - this.sentry_start_time) / 1000;
      ASSESSMENT_STATUSES.sentry_message += `,  Raise Hand Calibration time: ${diff} seconds`;
    }
  }

  /**
   * Checks if any person pose is within a central path region, indicating the path is blocked.
   * @param {Array} poses - Array of multi-person pose objects.
   * @returns {boolean} True if the path is clear (no one inside it), false if blocked.
   */
  path_checking(poses) {
    if (!poses || !Array.isArray(poses)) {
      // If poses isn't valid, assume the path is clear
      return true;
    }

    // Define the bounding box for the path
    const xMin = this.options.sw / 2 - 150;
    const xMax = this.options.sw / 2 + 150;
    const yMin = 1300;
    const yMax = this.camera.video.videoHeight;

    let fillxMin = this.camera.scale_to_fit({ x: xMin, y: 0 }).x;
    let fillyMin = this.camera.scale_to_fit({ x: 0, y: yMin }).y;
    let fillxMax = this.camera.scale_to_fit({ x: xMax, y: 0 }).x;
    let fillyMax = this.camera.scale_to_fit({ x: 0, y: yMax }).y;

    // For each person, check if any keypoint is in the path bounding box
    for (let person of poses) {
      console.log("person detected");
      if (person.score > 0.25) {
        for (let kp of person.keypoints) {
          if (kp.score > 0.25) {
            let scaledX = this.camera.scale_to_fit({ x: kp.x, y: 0 }).x;
            let scaledY = this.camera.scale_to_fit({ x: 0, y: kp.y }).y;
            if (
              scaledX >= fillxMin &&
              scaledX <= fillxMax &&
              scaledY >= fillyMin &&
              scaledY <= fillyMax
            ) {
              console.log("found another person in the path");
              return false;
            }
          }
        }
      }
    }

    // No one is in the path
    return true;
  }

  /**
   * Based on a stored angle value, sets an "extended start point" and associated line
   * for path checking or bounding box placement.
   */
  get_start_extended_points() {
    const y_axis = parseInt(ASSESSMENT_STATUSES.current_angle);

    const angleToYMap = {
      88: 1085,
      89: 1095,
      90: 1105,
      91: 1120,
      92: 1130,
    };

    const y_extended = angleToYMap[y_axis];
    this.calibration.start_point_extended_cv = new cv.Point(
      this.options.sw / 2,
      y_extended
    );

    this.calibration.start_line_extended_cv = {
      x1: Math.min(this.calibration.start_point_extended_cv.x - 50, 0),
      y1: this.calibration.start_point_extended_cv.y,
      x2: Math.max(
        this.calibration.start_point_extended_cv.x + 50,
        this.options.sw
      ),
      y2: this.calibration.start_point_extended_cv.y,
    };
  }

  /**
   * TODO: Refactor into a shared BoundingBoxRenderer utility class.
   * The drawing logic (roundRect, fill color, shoe print image, dotted border) is
   * duplicated across gait.js, tug.js, mCTSIB.js, and sitStandProcessing.js.
   * Each assessment calculates box coordinates differently (gait uses start_line_extended_cv,
   * TUG uses create_patient_box_at_distance with 3ft/13ft, mCTSIB uses 6ft, sitStandProcessing
   * uses fixed margins), but the rendering is identical. A shared class with a
   * draw(ctx, x, y, width, height, isPatientInBox) method would eliminate this duplication.
   */
  draw_PatientFeetBox(poses) {
    var c = document.getElementById("output");

    const sixFeetMeters = 6 * 0.3048;

    if (!this.calibration.complete()) {
      this.calibration.get_line_at_distance(sixFeetMeters);
    }

    // const threeFeetMeters = 3 * 0.3048;
    const box = this.calibration.create_patient_box_at_distance(sixFeetMeters);

    if (!box) {
      this.notEnoughSpace = true;
      return; // Don't draw if box is not found
    }

    let rect_start_x = Math.floor(c.width * this.side_margin) - 50;
    let scale_to_fit_the_point = this.camera.scale_to_fit_the_point(
      box.x1 + 100,
      box.y1
    );
    let rect_start_y = Math.floor(scale_to_fit_the_point.y);
    let rect_width = c.width * (1 - 2 * this.side_margin) + 100;
    let rect_height = this.camera.scale_to_fit_the_point(
      box.x1 + 100,
      box.y1 - FEET_BOX_LENGTH
    ).y;
    rect_height = -Math.abs(scale_to_fit_the_point.y - rect_height);

    this.patient_feet_box_x[0] = rect_start_x;
    this.patient_feet_box_x[1] = rect_start_x + rect_width;
    this.patient_feet_box_y[0] = box.y1 - FEET_BOX_LENGTH;
    this.patient_feet_box_y[1] = box.y1;

    this.ctx.beginPath();
    this.ctx.lineWidth = 5;

    // Buffer variable -> 30% above & below the box's middle point
    let patient_inner_buffer = Math.abs(
      (this.patient_feet_box_y[0] - this.patient_feet_box_y[1]) * 0.2
    );

    let patient_in_box = MultiPoseUtility.are_both_ankles_in_box(
      poses,
      this.patient_feet_box_x,
      [
        this.patient_feet_box_y[0] + patient_inner_buffer,
        this.patient_feet_box_y[1] - patient_inner_buffer,
      ],
      this.camera
    );

    // Fill color indicates whether someone is present in the box
    if (patient_in_box) {
      this.ctx.fillStyle = "rgba(0,128,0,0.75)";
    } else {
      this.ctx.fillStyle = "rgba(150,75,0,0.75)";
    }

    this.ctx.roundRect(rect_start_x, rect_start_y, rect_width, rect_height, 20);
    this.ctx.fill();

    // Draw shoe print image centered in the box
    if (this.shoe_print_loaded) {
      const imgAspect = this.shoe_print_img.width / this.shoe_print_img.height;
      const boxAbsHeight = Math.abs(rect_height);
      const imgHeight = boxAbsHeight * 0.8;
      const imgWidth = imgHeight * imgAspect;
      const imgX = rect_start_x + (rect_width - imgWidth) / 2;
      const imgY = rect_start_y + (rect_height - imgHeight) / 2;
      this.ctx.globalAlpha = 0.85;
      this.ctx.drawImage(this.shoe_print_img, imgX, imgY, imgWidth, imgHeight);
      this.ctx.globalAlpha = 1.0;
    }

    // Dotted border lines
    this.ctx.lineWidth = 5;
    this.ctx.setLineDash([10, 5]);
    this.ctx.strokeStyle = "rgba(0,0,0,0.75)";
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }

  /**
   * Draws a "path clearance" box over the canvas to visualize the region we check for blockages.
   * Animates a color fill from bottom to top.
   */
  draw_PathClearanceBox() {
    const c = document.getElementById("output");
    const ctx = c.getContext("2d");
    ctx.lineWidth = 3;

    // Create a vertical gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, c.height);
    gradient.addColorStop(0, "rgba(0, 255, 255, 0.2)");
    gradient.addColorStop(1, "rgba(255, 0, 255, 0.2)");

    ctx.fillStyle = gradient;
    ctx.strokeStyle = "magenta";

    const xMin = this.options.sw / 2 - 150;
    const xMax = this.options.sw / 2 + 150;
    const yMin = 1300;
    const yMax = this.camera.video.videoHeight;

    let fillxMin = this.camera.scale_to_fit({ x: xMin, y: 0 }).x;
    let fillyMin = this.camera.scale_to_fit({ x: 0, y: yMin }).y;
    let fillxMax = this.camera.scale_to_fit({ x: xMax, y: 0 }).x;
    let fillyMax = this.camera.scale_to_fit({ x: 0, y: yMax }).y;

    const ctx2 = c.getContext("2d");
    ctx2.beginPath();
    ctx2.strokeStyle = "yellow";
    ctx2.lineWidth = 3;
    ctx2.fillStyle = "rgba(255,255,0,0.2)";
    ctx2.fillRect(fillxMin, fillyMin, fillxMax - fillxMin, fillyMax - fillyMin);
    ctx2.rect(fillxMin, fillyMin, fillxMax - fillxMin, fillyMax - fillyMin);
    ctx2.stroke();
    ctx2.closePath();

    if (this.current_y < fillyMin) {
      this.current_y = fillyMax;
      this.old_y = fillyMax;
    }

    // Basic animation that moves upward
    const animate = () => {
      if (this.current_y < fillyMax) {
        ctx.clearRect(fillxMin, this.old_y - 5, fillxMax - fillxMin, 10);
      }
      ctx.beginPath();
      ctx.fillRect(
        fillxMin,
        this.current_y,
        fillxMax - fillxMin,
        fillyMax - this.current_y
      );
      ctx.rect(
        fillxMin,
        this.current_y,
        fillxMax - fillxMin,
        fillyMax - this.current_y
      );
      ctx.stroke();
      ctx.closePath();

      this.old_y = this.current_y;
      if (this.current_y > fillyMin) {
        this.current_y -= 2;
        requestAnimationFrame(animate);
      }
    };
    animate();
  }

  /**
   * Captures a frame (PNG) from the video, uploads it to S3,
   * then calls an external API to generate depth data. If it fails multiple times,
   * it raises an error, cancels the assessment, and plays an interruption audio.
   */
  async uploadImageToS3AndCallAPI() {
    try {
      let PatientId = localStorage.getItem("patientServerID");
      let image_name = `images/scene_depth_input_${PatientId}.jpg`;
      const s3Bucket = S3_BUCKET;
      const s3Key = `depths/scene_depth_${PatientId}.npy`;

      // Capture current frame to offscreen canvas
      this.offscreen_canvas.width = this.camera.video.videoWidth;
      this.offscreen_canvas.height = this.camera.video.videoHeight;
      this.offscreen_canvas_ctx.drawImage(
        this.camera.video,
        0,
        0,
        this.camera.video.videoWidth,
        this.camera.video.videoHeight
      );

      const imageUrl = this.offscreen_canvas.toDataURL("image/jpeg", 0.9);

      // Convert base64 to binary
      const base64Data = imageUrl.replace(/^data:image\/jpeg;base64,/, "");
      const binaryData = atob(base64Data);
      const binaryBuffer = new Uint8Array(binaryData.length);

      for (let i = 0; i < binaryData.length; i++) {
        binaryBuffer[i] = binaryData.charCodeAt(i);
      }

      // Upload the screenshot to S3
      const params = {
        Bucket: s3Bucket,
        Key: image_name,
        Body: binaryBuffer,
        ContentEncoding: "base64",
        ContentType: "image/jpeg",
      };
      await s3Client.send(new PutObjectCommand(params));
      console.log("Successfully uploaded data to S3");
      this.append_sentry_message("Successfully uploaded data to S3");

      const apiURL = API_URL;
      const postData = {
        image_path: image_name,
        model_selection: "vit-large",
        PatientId: PatientId,
        fx: 740,
        fy: 740,
      };

      // Retry logic for external API call
      let attempts = 3;
      while (attempts > 0) {
        try {
          const controller = new AbortController();
          const id = setTimeout(() => controller.abort(), 15000); // 15s timeout

          const response = await fetch(apiURL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(postData),
            signal: controller.signal,
          });

          clearTimeout(id);

          if (!response.ok) {
            this.append_sentry_message(
              `HTTP error! Status: ${response.status}`
            );
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          console.log("success from API");
          this.append_sentry_message("API call successful");

          // Load the generated .npy file from S3 into calibration
          this.is_uploaded = await this.calibration.get_depth_data(
            s3Bucket,
            s3Key
          );

          // If path was blocked while API was in flight, is_api_called was
          // reset to null — discard the stale depth data
          if (!this.is_api_called) {
            this.calibration.depthData = null;
            this.append_sentry_message("Discarding stale depth data — path was blocked during API call");
            return;
          }
          break;
        } catch (error) {
          console.error("Error in API call:", error);
          this.append_sentry_message("Error in API call");
          attempts--;
          if (attempts === 0) {
            console.error("API request failed after multiple attempts.");
            this.append_sentry_message(
              "API request failed after multiple attempts."
            );
            // Stop voice listening on API failure
            if (this.enabled_voice_listener) {
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.current_state = mCTSIBAssessmentStates.test_complete;
            document.getElementById("hdnRemoveGaitDontWalk")?.click();
            document.getElementById("hdnAssessementErrorPopup").click();
            ASSESSMENT_STATUSES.assessment_Completed = true;
            localStorage.removeItem("modalDisplayed");
            // this.audio_player.play(TugAudios.assessment_interrupted);
            return;
          } else {
            console.log(`Retrying... attempts left: ${attempts}`);
            this.append_sentry_message(
              `Retrying... attempts left: ${attempts}`
            );
          }
        }
      }
    } catch (err) {
      console.log("Error uploading data: ", err);
      this.append_sentry_message("Error uploading data to S3 bucket");
      // Stop voice listening on S3 upload error
      if (this.enabled_voice_listener) {
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
      }
      this.current_state = mCTSIBAssessmentStates.test_complete;
      document.getElementById("hdnRemoveGaitDontWalk")?.click();
      document.getElementById("hdnAssessementErrorPopup").click();
      ASSESSMENT_STATUSES.assessment_Completed = true;
      localStorage.removeItem("modalDisplayed");
      // this.audio_player.play(TugAudios.assessment_interrupted);
      return;
    }
  }

  /**
   * Advances to the next sub-assessment, resets UI and state,
   * and selects the appropriate next recording function.
   */
  switch_to_next_sub_assessment_state() {
    this.reset_pose_estimator();
    this.switch_to_multi_pose_callback();
    this.gesture_detector.reset();
    this.shown_persistent_icon = false;

    // Reset reference frame state for next assessment
    this.sway_reference_left = null;
    this.sway_reference_right = null;
    this.sway_frame_count = 0;
    this.reference_captured = false;
    this.ready_to_capture_reference = false;
    this.reference_frames_collected = null;
    this.reference_collection_count = 0;
    this.reference_collection_start_frame = 0;
    this.sway_x_data = [];
    this.sway_y_data = [];
    this.sway_smoothing_buffer = null;
    this.sway_ema_x = null;
    this.sway_ema_y = null;
    this.prev_raw_x = null;
    this.prev_raw_y = null;
    this.yawActive = null;
    // Reset torso quadrilateral reference metrics
    this.ref_left_hip = null;
    this.ref_right_hip = null;
    this.ref_shoulder_width = null;
    this.ref_hip_width = null;
    this.ref_left_edge = null;
    this.ref_right_edge = null;

    // console.log("Switching to next sub-assessment");

    switch (this.current_arom_sub_assessment) {
      case mCTSIB_ASSESSMENT.Open_Eyes:
        // this.current_state =
        //   mCTSIBAssessmentStates.all_sub_assessments_complete;
        // if (this.calibration_error_occured) {
        //   this.calibration_error_occured = false;
        // }

        this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Closed_Eyes;
        // this.assessment_function = (poses) =>
        //   recordRightShoulderFlexExt(this, poses);
        sessionStorage.setItem("phaseNo", "2");
        sessionStorage.setItem(
          "mCTSIBAssessmentIndex",
          mCTSIB_ASSESSMENT.Closed_Eyes
        );
        this.current_state = mCTSIBAssessmentStates.calibration_1;
        this.first_frame_time = this.current_time;
        this.patient_within_box_check = false;
        this.patient_within_box_timer = 0;
        this.within_bound_start_flag = false;
        this.assessment_timer = 0;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
        }
        break;

      case mCTSIB_ASSESSMENT.Closed_Eyes:
        this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Foam_Open_Eyes;
        // this.assessment_function = (poses) =>
        //   recordLeftElbowFlexionExtension(this, poses);
        sessionStorage.setItem("phaseNo", "3");
        sessionStorage.setItem(
          "mCTSIBAssessmentIndex",
          mCTSIB_ASSESSMENT.Foam_Open_Eyes
        );
        this.current_state = mCTSIBAssessmentStates.calibration_1;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
        }
        break;

      case mCTSIB_ASSESSMENT.Foam_Open_Eyes:
        this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Foam_Closed_Eyes;
        // this.assessment_function = (poses) =>
        //   recordLeftInternalExternalShoulderRotation(this, poses);
        sessionStorage.setItem("phaseNo", "4");
        sessionStorage.setItem(
          "mCTSIBAssessmentIndex",
          mCTSIB_ASSESSMENT.Foam_Closed_Eyes
        );
        this.current_state = mCTSIBAssessmentStates.calibration_1;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
        }
        break;

      case mCTSIB_ASSESSMENT.Foam_Closed_Eyes:
        this.current_state =
          mCTSIBAssessmentStates.all_sub_assessments_complete;
        if (this.calibration_error_occured) {
          this.calibration_error_occured = false;
        }
        break;

      default:
        break;
    }
  }

  /**
   * Checks for patient leaving or occlusion errors mid-assessment.
   * If errors exceed thresholds, aborts test and logs error.
   * @param {Array} poses - Array of detected poses.
   */
  assessment_error_check(poses) {
    let pose = this.detect_patient(poses);
    if (!poses || poses.length === 0) {
      this.frames_with_no_patient++;
    } else {
      this.frames_with_no_patient = 0;
      if (pose) {
        let left = this.camera.scale_to_fit({
          x: pose.leftAnkle.x,
          y: pose.leftAnkle.y,
        });
        let right = this.camera.scale_to_fit({
          x: pose.rightAnkle.x,
          y: pose.rightAnkle.y,
        });
        if (
          right.x > this.sit_stand_processing.extended_patient_box.x &&
          left.y > this.sit_stand_processing.extended_patient_box.y &&
          left.x <
            this.sit_stand_processing.extended_patient_box.x +
              this.sit_stand_processing.extended_patient_box.width &&
          right.y <
            this.sit_stand_processing.extended_patient_box.y +
              this.sit_stand_processing.extended_patient_box.height
        ) {
          this.frames_with_patient_outside_extended_box = 0;
        } else {
          this.frames_with_patient_outside_extended_box++;
        }
      }
    }
    if (
      this.frames_with_no_patient >= 15 ||
      this.frames_with_patient_outside_extended_box >= 3
    ) {
      this.end_time = this.current_time;
      let response = this.build_response();
      this.append_sentry_message(
        "Assessment response:" + JSON.stringify(response)
      );
      if (window["AssessmentPageReference"]) {
        window["AssessmentPageReference"].CompleteAssessment(response);
      }
      // Stop voice listening on assessment error
      this.cleanupVoiceListeners();
      this.current_state = mCTSIBAssessmentStates.test_complete;
      ASSESSMENT_STATUSES.assessment_error = true;
      ASSESSMENT_STATUSES.assessment_Completed = true;
      localStorage.removeItem("modalDisplayed");
      //   this.audio_player.play(mCTSIBAssessmentStates.assessment_interrupted);
      this.append_sentry_message(
        "Assessment ERROR occured - Someone has crossed the Patient during assessment or patient has left the Patient region."
      );
      this.frames_with_no_patient = 0;
      this.frames_with_patient_outside_extended_box = 0;
    }
  }

  /**
   * Allows therapist to restart the entire current phase,
   * resetting relevant metrics and UI state.
   */
  restart_phase_feature() {
    if (AromMenuOptions.restartPhase) {
      document.getElementById("output")?.classList.remove("mctsib-cam");
      AromMenuOptions.restartPhase = false;
      this.cleanupVoiceListeners();
      this.reset_pose_estimator();
      this.switch_to_multi_pose_callback();
      this.gesture_detector.reset();

      // Reset reference frame state and clear arrays for current sub-assessment
      this.sway_reference_left = null;
      this.sway_reference_right = null;
      this.sway_frame_count = 0;
      this.reference_captured = false;
      this.ready_to_capture_reference = false;
      this.reference_frames_collected = null;
      this.reference_collection_count = 0;
      this.reference_collection_start_frame = 0;
      this.sway_x_data = [];
      this.sway_y_data = [];
      this.sway_smoothing_buffer = null;
      this.sway_ema_x = null;
      this.sway_ema_y = null;
      this.prev_raw_x = null;
      this.prev_raw_y = null;
      this.yawActive = null;
      // Reset torso quadrilateral reference metrics
      this.ref_left_hip = null;
      this.ref_right_hip = null;
      this.ref_shoulder_width = null;
      this.ref_hip_width = null;
      this.ref_left_edge = null;
      this.ref_right_edge = null;
      // --------------------------- RESTART PHASE --------------------------------
      if (
        this.current_state === mCTSIBAssessmentStates.calibration_1 ||
        // this.current_state ===
        //   mCTSIBAssessmentStates.abduction_adduction_calibration_2 ||
        this.current_state === mCTSIBAssessmentStates.raise_hand ||
        this.current_state === mCTSIBAssessmentStates.gesture_detection ||
        this.current_state === mCTSIBAssessmentStates.pre_assessment ||
        this.current_state === mCTSIBAssessmentStates.assessment_In_progress ||
        this.current_state === mCTSIBAssessmentStates.post_assessment_modal
      ) {
        // hide real‑time angle UI
        // this.arom_realtime_angle_left.style.display = "none";
        // this.arom_realtime_angle_right.style.display = "none";
        if (
          this.current_state === mCTSIBAssessmentStates.assessment_In_progress
        ) {
          document.getElementById("btn-arom-assessment-counter").click();
        }
        // reset entire phase back to first sub‑assessment of current side
        if (this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Open_Eyes) {
          //   this.assessment_function = (poses) =>
          //     recordAdductionAbduction(this, poses);

          this.sway_open_eyes_x = [];
          this.sway_open_eyes_y = [];

          sessionStorage.setItem("phaseNo", "1");
          sessionStorage.setItem(
            "mCTSIBAssessmentIndex",
            mCTSIB_ASSESSMENT.Open_Eyes
          );
          this.current_state = mCTSIBAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else if (
          this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Closed_Eyes
        ) {
          this.sway_closed_eyes_x = [];
          this.sway_closed_eyes_y = [];

          // restart right‑side sequence
          this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Closed_Eyes;
          //   this.assessment_function = (poses) =>
          //     recordRightShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "2");
          sessionStorage.setItem(
            "mCTSIBAssessmentIndex",
            mCTSIB_ASSESSMENT.Closed_Eyes
          );
          this.current_state = mCTSIBAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else if (
          this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Foam_Open_Eyes
        ) {
          this.sway_open_eyes_foam_x = [];
          this.sway_open_eyes_foam_y = [];

          // restart left‑side sequence
          this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Foam_Open_Eyes;
          //   this.assessment_function = (poses) =>
          //     recordLeftShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "3");
          sessionStorage.setItem(
            "mCTSIBAssessmentIndex",
            mCTSIB_ASSESSMENT.Foam_Open_Eyes
          );
          this.current_state = mCTSIBAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else if (
          this.current_arom_sub_assessment ===
          mCTSIB_ASSESSMENT.Foam_Closed_Eyes
        ) {
          this.sway_closed_eyes_foam_x = [];
          this.sway_closed_eyes_foam_y = [];

          // restart left‑side sequence
          this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Foam_Closed_Eyes;
          //   this.assessment_function = (poses) =>
          //     recordLeftShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "4");
          sessionStorage.setItem(
            "mCTSIBAssessmentIndex",
            mCTSIB_ASSESSMENT.Foam_Closed_Eyes
          );
          this.current_state = mCTSIBAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        }
      }
    }
  }

  /**
   * Called from the error modal "Retry" button to restart the current phase
   * after a sway boundary error. Resets sway state, clears current phase data,
   * and transitions back to calibration_1 for the current sub-assessment.
   */
  retryCurrentPhase() {
    // Clear error flags so the assessment can resume
    ASSESSMENT_STATUSES.assessment_error = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;

    document.getElementById("output")?.classList.remove("mctsib-cam");
    this.cleanupVoiceListeners();
    this.audio_player.stop();
    this.audio_player.current_playing_audio = null;
    this.reset_pose_estimator();
    this.switch_to_multi_pose_callback();
    this.gesture_detector.reset();

    // Reset sway reference frame state
    this.sway_reference_left = null;
    this.sway_reference_right = null;
    this.sway_frame_count = 0;
    this.reference_captured = false;
    this.ready_to_capture_reference = false;
    this.reference_frames_collected = null;
    this.reference_collection_count = 0;
    this.reference_collection_start_frame = 0;
    this.sway_x_data = [];
    this.sway_y_data = [];
    this.sway_smoothing_buffer = null;
    this.sway_ema_x = null;
    this.sway_ema_y = null;
    this.prev_raw_x = null;
    this.prev_raw_y = null;
    this.yawActive = null;
    this.ref_left_hip = null;
    this.ref_right_hip = null;
    this.ref_shoulder_width = null;
    this.ref_hip_width = null;
    this.ref_left_edge = null;
    this.ref_right_edge = null;

    // Clear current phase sway data and restart from calibration_1
    if (this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Open_Eyes) {
      this.sway_open_eyes_x = [];
      this.sway_open_eyes_y = [];
      sessionStorage.setItem("phaseNo", "1");
      sessionStorage.setItem("mCTSIBAssessmentIndex", mCTSIB_ASSESSMENT.Open_Eyes);
    } else if (this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Closed_Eyes) {
      this.sway_closed_eyes_x = [];
      this.sway_closed_eyes_y = [];
      sessionStorage.setItem("phaseNo", "2");
      sessionStorage.setItem("mCTSIBAssessmentIndex", mCTSIB_ASSESSMENT.Closed_Eyes);
    } else if (this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Foam_Open_Eyes) {
      this.sway_open_eyes_foam_x = [];
      this.sway_open_eyes_foam_y = [];
      sessionStorage.setItem("phaseNo", "3");
      sessionStorage.setItem("mCTSIBAssessmentIndex", mCTSIB_ASSESSMENT.Foam_Open_Eyes);
    } else if (this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Foam_Closed_Eyes) {
      this.sway_closed_eyes_foam_x = [];
      this.sway_closed_eyes_foam_y = [];
      sessionStorage.setItem("phaseNo", "4");
      sessionStorage.setItem("mCTSIBAssessmentIndex", mCTSIB_ASSESSMENT.Foam_Closed_Eyes);
    }

    this.current_state = mCTSIBAssessmentStates.calibration_1;
    this.first_frame_time = this.current_time;
    this.patient_within_box_check = false;
    this.patient_within_box_timer = 0;
    this.within_bound_start_flag = false;
    this.assessment_timer = this.current_time;
    this.play_beep_sound = true;
    this.handFlag = true;
    this.shown_persistent_icon = false;
    this.display_modalFaceCamera = false;
    this.enabled_voice_listener = false;
    this.voice_command_received = false;

    this.append_sentry_message(
      "mCTSIB retryCurrentPhase: restarting " +
      this.current_arom_sub_assessment.toString() + " from calibration_1"
    );
  }

  /**
   * Allows therapist to skip the current phase,
   * advancing directly to the next stage.
   */
  skip_phase_feature() {
    if (AromMenuOptions.skipPhase) {
      document.getElementById("output")?.classList.remove("mctsib-cam");
      AromMenuOptions.skipPhase = false;
      this.cleanupVoiceListeners();
      this.reset_pose_estimator();
      this.switch_to_multi_pose_callback();
      this.gesture_detector.reset();
      // --------------------------- SKIP PHASE --------------------------------
      if (
        this.current_state === mCTSIBAssessmentStates.calibration_1 ||
        // this.current_state ===
        //   mCTSIBAssessmentStates.abduction_adduction_calibration_2 ||
        this.current_state === mCTSIBAssessmentStates.raise_hand ||
        this.current_state === mCTSIBAssessmentStates.gesture_detection ||
        this.current_state === mCTSIBAssessmentStates.pre_assessment ||
        this.current_state === mCTSIBAssessmentStates.assessment_In_progress ||
        this.current_state === mCTSIBAssessmentStates.post_assessment_modal
      ) {
        // this.arom_realtime_angle_left.style.display = "none";
        // this.arom_realtime_angle_right.style.display = "none";
        if (
          this.current_state === mCTSIBAssessmentStates.assessment_In_progress
        ) {
          document.getElementById("btn-arom-assessment-counter").click();
        }
        if (this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Open_Eyes) {
          // skip straight to right‑shoulder flex/ext
          this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Closed_Eyes;
          //   this.assessment_function = (poses) =>
          //     recordRightShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "2");
          sessionStorage.setItem(
            "mCTSIBAssessmentIndex",
            mCTSIB_ASSESSMENT.Closed_Eyes
          );
          this.current_state = mCTSIBAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else if (
          this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Closed_Eyes
        ) {
          // skip to left‑shoulder flex/ext
          this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Foam_Open_Eyes;
          //   this.assessment_function = (poses) =>
          //     recordLeftShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "3");
          sessionStorage.setItem(
            "mCTSIBAssessmentIndex",
            mCTSIB_ASSESSMENT.Foam_Open_Eyes
          );
          this.current_state = mCTSIBAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else if (
          this.current_arom_sub_assessment === mCTSIB_ASSESSMENT.Foam_Open_Eyes
        ) {
          // skip to left‑shoulder flex/ext
          this.current_arom_sub_assessment = mCTSIB_ASSESSMENT.Foam_Closed_Eyes;
          //   this.assessment_function = (poses) =>
          //     recordLeftShoulderFlexExt(this, poses);
          sessionStorage.setItem("phaseNo", "4");
          sessionStorage.setItem(
            "mCTSIBAssessmentIndex",
            mCTSIB_ASSESSMENT.Foam_Closed_Eyes
          );
          this.current_state = mCTSIBAssessmentStates.calibration_1;
          document.getElementById("hdnFaceCameraPalmForwardPhase1").click();
          this.first_frame_time = this.current_time;
          this.patient_within_box_check = false;
          this.patient_within_box_timer = 0;
          this.within_bound_start_flag = false;
          this.assessment_timer = 0;
        } else {
          // if already on last sub‑assessment, mark complete
          this.current_state =
            mCTSIBAssessmentStates.all_sub_assessments_complete;
        }
      }
    }
  }
}

export async function mCTSIBSentryLogging() {
  SentryLogging("mCTSIB");
}

export default mCTSIB;
